/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   CapacityMetricsDashboard.tsx
 *     Description     :   Main capacity metrics dashboard page
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-19
 *     Modified        :   2026-06-14 | Conformed to Spec 197 design blueprint — migrated
 *                         off MUI (Container/Paper/Grid/Tabs/Select/TextField/Alert) onto
 *                         the canonical Tailwind+token UI kit (PageLayout/Card/DataTable/
 *                         StatCard/Input/Select/Badge/states). Presentation-only; no data,
 *                         logic, state, route, or behavior changes. TrendSparkline retained.
 *     Constitution    :   v1.19.0
 *     Feature         :   030-capacity-metrics (T060-T061)
 * =========================================================================================
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Search, RefreshCw, AlertTriangle, Gauge } from 'lucide-react';
import { useCapacityDashboard, useActiveAlerts } from '../../hooks/useCapacityMetrics';
import TrendSparkline from '../../components/capacity/TrendSparkline';
import type { DashboardFilters, CapacityAlert } from '../../services/capacityMetricsApi';
import type { CapacityDashboardSummary } from '../../../../shared/types/capacityMetrics';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Select,
  Badge,
  StatCard,
  EmptyState,
  ErrorState,
  LoadingState,
  AppTooltip,
  PageHelpPanel,
  Extended,
  QuickGlance,
  Tabs,
} from '../../components/ui';
import type { DataTableColumn, TabItem } from '../../components/ui';
import { capacityHelp } from '../../data/help/capacityHelp';
import { formatEastern } from '@/utils/dateFormatter';

// ============================================================================
// Types
// ============================================================================

type CapacityTypeFilter = 'all' | 'Reference' | 'ClientDedicated' | 'MultiTenant' | 'Platform';
type AlertStatusFilter = 'all' | 'ok' | 'warning' | 'critical';

// ============================================================================
// Status helpers
// ============================================================================

const statusVariant: Record<string, 'success' | 'warning' | 'error' | 'secondary'> = {
  normal: 'success',
  warning: 'warning',
  critical: 'error',
  paused: 'secondary',
  unknown: 'secondary',
};

const statusLabel: Record<string, string> = {
  normal: 'Normal',
  warning: 'Warning',
  critical: 'Critical',
  paused: 'Paused',
  unknown: 'Unknown',
};

function alertBadgeVariant(severity: CapacityAlert['severity']): 'error' | 'warning' | 'info' {
  if (severity === 'CRITICAL') return 'error';
  if (severity === 'HIGH') return 'warning';
  return 'info';
}

// ============================================================================
// Component
// ============================================================================

export const CapacityMetricsDashboard: React.FC = () => {
  const navigate = useNavigate();

  // State for filters
  const [capacityTypeFilter, setCapacityTypeFilter] = useState<CapacityTypeFilter>('all');
  const [alertStatusFilter, setAlertStatusFilter] = useState<AlertStatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Build API filters
  const apiFilters: DashboardFilters = useMemo(() => {
    const filters: DashboardFilters = {};
    if (capacityTypeFilter !== 'all') {
      filters.capacity_type = capacityTypeFilter;
    }
    if (alertStatusFilter !== 'all') {
      filters.alert_status = alertStatusFilter;
    }
    return filters;
  }, [capacityTypeFilter, alertStatusFilter]);

  // Fetch dashboard data
  const { data: dashboardData, isLoading, error, refetch } = useCapacityDashboard(apiFilters);
  const { data: alertsData } = useActiveAlerts();

  // Filter capacities by search query (client-side)
  const filteredCapacities = useMemo(() => {
    if (!dashboardData?.capacities) return [];
    if (!searchQuery.trim()) return dashboardData.capacities;

    const query = searchQuery.toLowerCase();
    return dashboardData.capacities.filter(
      (capacity) =>
        capacity.capacity_name.toLowerCase().includes(query) ||
        capacity.capacity_type.toLowerCase().includes(query) ||
        capacity.region?.toLowerCase().includes(query)
    );
  }, [dashboardData?.capacities, searchQuery]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const capacities = dashboardData?.capacities || [];
    return {
      total: capacities.length,
      normal: capacities.filter((c) => c.status === 'normal').length,
      warning: capacities.filter((c) => c.status === 'warning').length,
      critical: capacities.filter((c) => c.status === 'critical').length,
      paused: capacities.filter((c) => c.status === 'paused').length,
      unknown: capacities.filter((c) => c.status === 'unknown').length,
      activeAlerts: alertsData?.total || 0,
    };
  }, [dashboardData?.capacities, alertsData?.total]);

  // Table columns (widths sum to ~100%)
  const columns: DataTableColumn<CapacityDashboardSummary>[] = [
    {
      key: 'capacity_name',
      header: 'Capacity Name',
      width: '24%',
      render: (c) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-[var(--color-text-primary)]" title={c.capacity_name}>
            {c.capacity_name}
          </div>
          {c.region && (
            <div className="text-xs text-[var(--color-text-secondary)]">{c.region}</div>
          )}
        </div>
      ),
    },
    {
      key: 'capacity_type',
      header: 'Type',
      width: '14%',
      render: (c) => (
        <Badge variant="secondary" isOutlined>
          {c.capacity_type}
        </Badge>
      ),
    },
    {
      key: 'cu_used_percent',
      header: 'CU Usage',
      align: 'right',
      width: '13%',
      render: (c) => (
        <div>
          {c.current_cu_used_percent === null ? (
            // Nothing measured this capacity (Spec 286 T160 item 5): "no data", never an idle 0%.
            <div className="text-[var(--color-text-secondary)]">no data</div>
          ) : (
            <div
              className={clsx(
                'font-medium',
                c.current_cu_used_percent >= 95
                  ? 'text-[var(--color-status-danger)]'
                  : c.current_cu_used_percent >= 80
                    ? 'text-[var(--color-status-warning)]'
                    : 'text-[var(--color-text-primary)]',
              )}
            >
              {c.current_cu_used_percent.toFixed(1)}%
            </div>
          )}
          <div className="text-xs text-[var(--color-text-secondary)]">
            {c.cu_allocation === null ? '—' : `${c.cu_allocation} CU`}
          </div>
        </div>
      ),
    },
    {
      key: 'trend',
      header: '7-Day Trend',
      align: 'center',
      width: '18%',
      render: (c) => (
        <div className="flex justify-center">
          <TrendSparkline data={c.sparkline_data} width={140} height={40} />
        </div>
      ),
    },
    {
      key: 'storage_gb',
      header: 'Storage',
      align: 'right',
      width: '11%',
      render: (c) => `${c.current_storage_gb.toFixed(1)} GB`,
    },
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      width: '12%',
      render: (c) => (
        <Badge variant={statusVariant[c.status] ?? 'secondary'}>
          {statusLabel[c.status] ?? c.status}
        </Badge>
      ),
    },
    {
      key: 'active_workspaces',
      header: 'Workspaces',
      align: 'right',
      width: '8%',
      render: (c) => c.active_workspaces,
    },
  ];

  const tabs: TabItem[] = [
    { key: 'overview', label: 'Capacity Overview' },
    { key: 'alerts', label: `Active Alerts (${summaryStats.activeAlerts})` },
  ];

  return (
    <PageLayout
      title="Capacities"
      description="Monitor Fabric capacity utilization, trends, and alerts"
      icon={<Gauge className="h-6 w-6" />}
      actions={
        <>
          <PageHelpPanel content={capacityHelp} buttonSize="sm" />
          <Button
            variant="outline"
            leftIcon={<RefreshCw className="h-4 w-4" />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </>
      }
    >
      {error ? (
        <ErrorState
          title="Failed to load capacity metrics"
          message="Please try again later."
          onRetry={() => refetch()}
        />
      ) : (
        <>
          {/* Summary tiles */}
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
            <AppTooltip title="Total number of Fabric capacities being monitored">
              <StatCard
                label="Total Capacities"
                value={isLoading ? '—' : summaryStats.total}
                subtext={!isLoading && summaryStats.unknown > 0 ? `${summaryStats.unknown} not measured` : undefined}
              />
            </AppTooltip>
            <StatCard
              label="Normal"
              value={isLoading ? '—' : summaryStats.normal}
              valueClassName="text-[var(--color-status-success)]"
            />
            <StatCard
              label="Warning"
              value={isLoading ? '—' : summaryStats.warning}
              valueClassName="text-[var(--color-status-warning)]"
            />
            <StatCard
              label="Critical"
              value={isLoading ? '—' : summaryStats.critical}
              valueClassName="text-[var(--color-status-danger)]"
            />
            <StatCard
              label="Active Alerts"
              value={isLoading ? '—' : summaryStats.activeAlerts}
              valueClassName={
                summaryStats.activeAlerts > 0
                  ? 'text-[var(--color-status-danger)]'
                  : undefined
              }
              icon={
                summaryStats.activeAlerts > 0 ? (
                  <AlertTriangle className="h-4 w-4 text-[var(--color-status-danger)]" />
                ) : undefined
              }
            />
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for the full details.
            </Card>
          </QuickGlance>

          <Extended>
          {/* Filters and Search */}
          <Card className="mb-6 p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-end">
              <div className="md:col-span-4">
                <Input
                  isFullWidth
                  placeholder="Search capacities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftAddon={<Search className="h-4 w-4" />}
                />
              </div>
              <div className="md:col-span-3">
                <AppTooltip title="Filter capacities by their type classification">
                  <Select
                    isFullWidth
                    label="Capacity Type"
                    value={capacityTypeFilter}
                    onChange={(e) => setCapacityTypeFilter(e.target.value as CapacityTypeFilter)}
                    options={[
                      { value: 'all', label: 'All Types' },
                      { value: 'Reference', label: 'Reference' },
                      { value: 'ClientDedicated', label: 'Client Dedicated' },
                      { value: 'MultiTenant', label: 'Multi-Tenant' },
                      { value: 'Platform', label: 'Platform' },
                    ]}
                  />
                </AppTooltip>
              </div>
              <div className="md:col-span-3">
                <Select
                  isFullWidth
                  label="Status"
                  value={alertStatusFilter}
                  onChange={(e) => setAlertStatusFilter(e.target.value as AlertStatusFilter)}
                  options={[
                    { value: 'all', label: 'All Statuses' },
                    { value: 'ok', label: 'Normal' },
                    { value: 'warning', label: 'Warning' },
                    { value: 'critical', label: 'Critical' },
                  ]}
                />
              </div>
              <div className="text-sm text-[var(--color-text-secondary)] md:col-span-2">
                {filteredCapacities.length} of {summaryStats.total} capacities
              </div>
            </div>
          </Card>

          {/* Tabs (kit Tabs: role="tablist"/"tab", as the MUI Tabs it replaced had) */}
          <Tabs tabs={tabs} value={activeTab} onChange={setActiveTab} className="mb-4" />

          {/* Tab content */}
          {activeTab === 'overview' && (
            <Card padding="none">
              {isLoading ? (
                <div className="p-4">
                  <LoadingState rows={6} />
                </div>
              ) : (
                <DataTable
                  columns={columns}
                  rows={filteredCapacities}
                  rowKey={(c) => c.capacity_id}
                  onRowClick={(c) => navigate(`/capacity-metrics/${c.capacity_id}`)}
                  minWidth="52rem"
                  emptyMessage="No capacities found"
                />
              )}
            </Card>
          )}

          {activeTab === 'alerts' && (
            <Card className="p-4">
              {alertsData?.alerts && alertsData.alerts.length > 0 ? (
                <div className="space-y-3">
                  {alertsData.alerts.map((alert) => (
                    <div
                      key={alert.id}
                      className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-3"
                    >
                      <div className="flex items-center gap-2">
                        <Badge variant={alertBadgeVariant(alert.severity)}>{alert.severity}</Badge>
                        <span className="font-medium text-[var(--color-text-primary)]">
                          {alert.capacity_name} — {alert.alert_type}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                        Threshold: {alert.threshold_breached}% | Current:{' '}
                        {alert.current_value.toFixed(1)}%
                      </p>
                      <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                        Triggered: {formatEastern(alert.triggered_at)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState title="No active alerts" />
              )}
            </Card>
          )}
          </Extended>
        </>
      )}
    </PageLayout>
  );
};

export default CapacityMetricsDashboard;
